/**
 * Sidebar Component — Premium Global Restaurant Discovery
 * Rined layout with luxury spacing and interactions
 */
import React from 'react';
import { Search, X, Heart } from 'lucide-react';
import { Badge } from '../shared';

// Category emoji mapping
const CATEGORY_ICONS = {
  'Street Food': '🍜', 'Pad Thai': '🍜', 'Beef Noodle': '🍲',
  'Noodles': '🍜', 'Vegan': '🥬', 'Thai': '🍛', 'Chinese': '🥟',
  'Indian': '🍛', 'Fusion': '✨', 'Japanese': '🍣', 'International': '🌍',
};

function getCategoryIcon(category) {
  return CATEGORY_ICONS[category] || '🍽️';
}

export function Sidebar({
  restaurants,
  selectedRestaurant,
  onSelectRestaurant,
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
  dietaryFilter,
  onDietaryFilterChange,
  favorites,
  t,
  onBadgeClick,
  children,
  onCommunityClick
}) {
  return (
    <div className={`absolute z-20 flex flex-col bg-white/[0.98] backdrop-blur-2xl transition-transform duration-500 ease-spring pointer-events-auto
      md:top-4 md:left-4 md:bottom-[5.5rem] md:w-[400px] md:h-auto md:max-h-[calc(100vh-2rem-5.5rem)] md:rounded-3xl md:shadow-spring
      bottom-0 left-0 right-0 h-[65vh] rounded-t-[32px] shadow-[0_-8px_40px_rgba(0,0,0,0.12)]
      ${selectedRestaurant ? 'translate-y-[120%] md:translate-y-0 md:-translate-x-[120%] pointer-events-none' : 'translate-y-0'}
    `}>
      {/* Header Section */}
      <div className="px-5 pt-5 pb-4 bg-gradient-to-b from-white via-white to-slate-50/30 md:rounded-t-3xl rounded-t-[32px] rounded-b-2xl shrink-0 z-10 shadow-sm relative">
        {/* Mobile handle */}
        <div className="w-10 h-1.5 bg-neutral-200 rounded-full mx-auto mb-4 md:hidden"></div>

        {children}

        {/* Search - Premium Floating Style */}
        <div className="relative mt-4 group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-coral-500 transition-colors duration-400" size={18} strokeWidth={2.5} />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full bg-white pl-12 pr-10 py-4 font-medium rounded-[20px] text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none shadow-soft focus:shadow-spring border-2 border-slate-100 focus:border-coral-100 transition-all duration-500 ease-spring"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-300 hover:text-neutral-600 transition-colors duration-200"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Unified Filter Row - Refined Pills */}
        <div className="flex gap-2 mt-5 overflow-x-auto pb-2 scrollbar-hide mask-fade-right">
          <FilterChip
            active={filter === 'all' && !dietaryFilter}
            onClick={() => { onFilterChange('all'); onDietaryFilterChange(null); }}
          >
            {t('all')}
          </FilterChip>
          <FilterChip
            active={filter === 'michelin' && !dietaryFilter}
            onClick={() => { onFilterChange('michelin'); onDietaryFilterChange(null); }}
            icon="★"
            gold
          >
            Michelin
          </FilterChip>
          <FilterChip
            active={filter === 'favorites'}
            onClick={() => { onFilterChange('favorites'); onDietaryFilterChange(null); }}
            icon="♥"
          >
            {favorites.length}
          </FilterChip>

          <div className="w-px bg-neutral-200/80 mx-1 flex-shrink-0 h-6 self-center" />

          <FilterChip
            active={dietaryFilter === 'vegan'}
            onClick={() => onDietaryFilterChange(dietaryFilter === 'vegan' ? null : 'vegan')}
            icon="🌱"
          >
            {t('vegan')}
          </FilterChip>
          <FilterChip
            active={dietaryFilter === 'halal'}
            onClick={() => onDietaryFilterChange(dietaryFilter === 'halal' ? null : 'halal')}
            icon="🕌"
          >
            {t('halal')}
          </FilterChip>
          <FilterChip
            active={dietaryFilter === 'glutenFree'}
            onClick={() => onDietaryFilterChange(dietaryFilter === 'glutenFree' ? null : 'glutenFree')}
            icon="🌾"
          >
            {t('glutenFree')}
          </FilterChip>
        </div>

        {/* Restaurant count - Refined */}
        <div className="flex justify-between items-center mt-3 px-1">
          <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-medium">
            {restaurants.length} Curated Spots
          </p>
        </div>
      </div>

      {/* Restaurant List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-4 pb-4">
        {restaurants.length > 0 ? (
          <div className="space-y-2.5 mt-2">
            {restaurants.map((restaurant, index) => (
              <div
                key={restaurant.id}
                onClick={() => onSelectRestaurant(restaurant)}
                className={`group relative p-4 cursor-pointer rounded-3xl transition-all duration-500 ease-spring ${selectedRestaurant?.id === restaurant.id
                  ? 'bg-slate-900 shadow-spring scale-[1.02] z-10'
                  : 'hover:bg-white hover:shadow-spring hover:-translate-y-1 bg-transparent'
                  }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start gap-4">
                  {/* Category Icon */}
                  <div className={`w-16 h-16 rounded-[20px] flex items-center justify-center text-2xl flex-shrink-0 transition-all duration-500 ease-spring ${selectedRestaurant?.id === restaurant.id
                    ? 'bg-white/10 text-white shadow-inner'
                    : 'bg-white shadow-soft group-hover:shadow-md group-hover:scale-105'
                    }`}>
                    {getCategoryIcon(restaurant.category)}
                  </div>

                  <div className="flex-1 min-w-0 py-1">
                    <div className="flex justify-between items-start">
                      <h3 className={`font-display font-bold text-[16px] truncate leading-tight tracking-tight transition-colors duration-300 ${selectedRestaurant?.id === restaurant.id ? 'text-white' : 'text-slate-900'
                        }`}>
                        {restaurant.name}
                      </h3>
                      {favorites.includes(restaurant.id) && (
                        <Heart size={16} className={`fill-current ml-2 flex-shrink-0 ${selectedRestaurant?.id === restaurant.id ? 'text-coral-400' : 'text-coral-500'
                          }`} />
                      )}
                    </div>

                    <p className={`text-[12px] mt-1.5 transition-colors duration-300 ${selectedRestaurant?.id === restaurant.id ? 'text-neutral-400' : 'text-neutral-500'
                      }`}>
                      {restaurant.category}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {restaurant.badges?.map(b => (
                        <Badge
                          key={b}
                          type={b}
                          t={t}
                          size="sm"
                          className={selectedRestaurant?.id === restaurant.id ? '!bg-luxury-slate !text-neutral-300 !border-luxury-slate' : ''}
                          onClick={(e) => {
                            e.stopPropagation();
                            onBadgeClick && onBadgeClick();
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center p-10 text-center opacity-60">
            <div className="text-5xl mb-5 grayscale opacity-50">◈</div>
            <p className="text-sm font-medium text-luxury-charcoal">{t('noResults')}</p>
            <p className="text-xs mt-2 text-neutral-500 max-w-[200px] leading-relaxed">{t('addRestaurants')}</p>
          </div>
        )}
      </div>

      {/* Version Footer - Refined */}
      <div className="px-5 py-3 border-t border-neutral-100 bg-white/90 backdrop-blur-sm flex justify-between items-center rounded-b-2xl shrink-0">
        <span className="font-mono text-[10px] text-neutral-300 tracking-wider">v4.2.0-GLOBAL</span>
        <span className="font-display text-[10px] tracking-[0.25em] text-neutral-300 uppercase">TasteTrace</span>
      </div>
    </div>
  );
}

/**
 * FilterChip — Premium filter pill with luxury interactions
 */
function FilterChip({ active, onClick, icon, children, gold }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-medium whitespace-nowrap transition-all duration-400 border ${active
        ? gold
          ? 'bg-accent-gold text-white border-accent-gold shadow-lg shadow-accent-gold/25'
          : 'bg-luxury-charcoal text-white border-luxury-charcoal shadow-lg shadow-luxury-charcoal/20'
        : 'bg-white text-neutral-500 border-neutral-200/80 hover:border-neutral-300 hover:text-luxury-charcoal hover:shadow-soft'
        }`}
    >
      {icon && <span className={`text-[10px] leading-none ${gold && !active ? 'text-accent-gold' : ''}`}>{icon}</span>}
      {children}
    </button>
  );
}

export default Sidebar;
